import { createContext, useContext, useMemo, useState } from 'react';
import { App as AntdApp } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Card } from 'components/common';
import { UserPermissions, getUser } from 'store';
import { Compensation } from 'types';
import { CompensationStatus, Namespace } from 'enums';
import { useAppDispatch, useAppSelector, useNotification } from 'hooks';

import { Details } from '../Details';
import { CompensationHeader } from './CompensationHeader';
import { CompensationBreadcrumbsProps } from './CompensationBreadcrumbs';
import { CompensationEditMode } from '../Details/enums';
import { fetchCurrentYearRequests, useDeleteRequestMutation } from '../../services';

interface Props {
  readonly breadcrumbs: CompensationBreadcrumbsProps;
  readonly permissions: UserPermissions;
  readonly isAccountPage?: boolean;
}

interface Context {
  readonly isEditMode: boolean;
  readonly setIsEditMode: (isEditMode: boolean) => void;
  readonly availableEditMode: CompensationEditMode;
  readonly setAvailableEditMode: (mode: CompensationEditMode) => void;
  readonly permissions: UserPermissions;
  readonly compensation?: Compensation;
  readonly setCompensation: (comp?: Compensation) => void;
  readonly isAccountPage: boolean;
}

const CompensationPageContext = createContext<Context | null>(null);

export const useCompensationPage = () => useContext(CompensationPageContext)!;

export const CompensationPage = ({ breadcrumbs, permissions, isAccountPage = false }: Props) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [availableEditMode, setAvailableEditMode] = useState<CompensationEditMode>(CompensationEditMode.None);
  const [compensation, setCompensation] = useState<Compensation | undefined>(undefined);
  const dispatch = useAppDispatch();

  const { t } = useTranslation(Namespace.Common);
  const navigate = useNavigate();
  const { id } = useParams();
  const currentUser = useAppSelector(getUser)!;
  const sendNotification = useNotification();
  const [deleteRequest, { isLoading: isDeleting }] = useDeleteRequestMutation();

  const canDelete = (() => {
    if (!compensation) return false;
    const isCurrentUserCompensation = compensation.employeeID === currentUser.employeeID;
    const userStatusList = [CompensationStatus.Pending, CompensationStatus.UpdateRequired];
    const managerStatusList = [
      CompensationStatus.Pending,
      CompensationStatus.UpdateRequired,
      CompensationStatus.Rejected,
    ];

    if (isCurrentUserCompensation) return userStatusList.includes(compensation.status);

    if (permissions.hasManageCompensationsPermission || permissions.hasAdministratorPermission)
      return managerStatusList.includes(compensation.status);

    return false;
  })();

  const { modal } = AntdApp.useApp();

  const onDelete = () => {
    modal.confirm({
      title: t('deleteRequestConfirm'),
      okText: t('delete'),
      cancelText: t('cancel'),
      onOk: async () => {
        if (!id) return;

        try {
          await deleteRequest(Number(id)).unwrap();

          if (
            !isAccountPage ||
            permissions.hasManageCompensationsPermission ||
            permissions.hasAdministratorPermission
          ) {
            dispatch(fetchCurrentYearRequests());
          }

          sendNotification(t('deletedRequestNotification'), 'success');
          navigate(-1);
        } catch (err) {
          sendNotification(t('somethingWentWrong'), 'error');
        }
      },
    });
  };

  const contextValue = useMemo(
    () => ({
      isEditMode,
      setIsEditMode,
      availableEditMode,
      setAvailableEditMode,
      permissions,
      compensation,
      setCompensation,
      isAccountPage,
    }),
    [
      isEditMode,
      setIsEditMode,
      availableEditMode,
      setAvailableEditMode,
      permissions,
      compensation,
      setCompensation,
      isAccountPage,
    ],
  );

  return (
    <Card>
      <CompensationPageContext.Provider value={contextValue}>
        <CompensationHeader
          breadcrumbs={breadcrumbs}
          isEditMode={isEditMode}
          availableEditMode={availableEditMode}
          onEditClick={() => setIsEditMode(true)}
          canDelete={canDelete}
          onDelete={onDelete}
          isDeleting={isDeleting}
        />
        <Details />
      </CompensationPageContext.Provider>
    </Card>
  );
};
